import React, { useState, useEffect } from 'react';
import { useTranslation } from 'react-i18next';
import { latestNews as fallbackNews, circulars as fallbackCirculars, tenders as fallbackTenders, importantNotices } from '../../../data/announcements';
import { useScrollReveal } from '../../../hooks/useScrollReveal';
import { usePortal } from '../PortalContext';
import { SectionHeading } from './SectionHeading';

type Tab = 'news' | 'circulars' | 'tenders';

const formatDate = (d: string) => {
  const date = new Date(d);
  const day = String(date.getDate()).padStart(2, '0');
  const months = ['Jan', 'Feb', 'Mar', 'Apr', 'May', 'Jun', 'Jul', 'Aug', 'Sep', 'Oct', 'Nov', 'Dec'];
  return `${day}-${months[date.getMonth()]}`;
};

const tenderStatusColor: Record<string, string> = {
  Open: 'bg-green-100 text-green-700',
  'Closing Soon': 'bg-[var(--portal-accent-soft2)] text-[color:var(--portal-accent-dark)]',
  Closed: 'bg-gray-100 text-gray-500',
};

export const AnnouncementSection: React.FC = () => {
  const { t } = useTranslation();
  const [activeTab, setActiveTab] = useState<Tab>('news');
  const { ref, isRevealed } = useScrollReveal({ threshold: 0.1 });
  const { siteId, publicApi } = usePortal();
  const [notifications, setNotifications] = useState<any[]>([]);

  useEffect(() => {
    publicApi.get(`/notifications?siteId=${siteId}`)
      .then(res => {
        const data = res.data?.data || [];
        if (data.length > 0) {
          setNotifications(data);
        }
      })
      .catch(() => {});
  }, [siteId]);

  const tabItems: { id: Tab; labelKey: string }[] = [
    { id: 'news', labelKey: 'announcements.tabNews' },
    { id: 'circulars', labelKey: 'announcements.tabCirculars' },
    { id: 'tenders', labelKey: 'announcements.tabTenders' },
  ];

  const getItems = () => {
    // Use API notifications if available, otherwise fall back to hardcoded
    if (notifications.length > 0) {
      return notifications.map((n: any) => ({
        title: n.ntfctn_dscn || n.title || '',
        date: n.efct_ts || n.i_ts || '',
        tag: n.url_txt || undefined,
        status: undefined,
      }));
    }
    switch (activeTab) {
      case 'news': return fallbackNews.map((n) => ({ title: n.title, date: n.date, tag: undefined, status: undefined }));
      case 'circulars': return fallbackCirculars.map((c) => ({ title: c.title, date: c.date, tag: c.ref, status: undefined }));
      case 'tenders': return fallbackTenders.map((t) => ({ title: t.title, date: t.date, tag: t.ref, status: t.status }));
    }
  };

  return (
    <section
      ref={ref as React.RefObject<HTMLElement>}
      className={`scroll-reveal portal-section portal-surface-alt portal-rule ${isRevealed ? 'revealed' : ''}`}
    >
      <div className="reveal-content portal-container">
        <SectionHeading
          eyebrow="Notice Board"
          title={t('announcements.title')}
          subtitle="Departmental news, circulars, tenders and urgent notices — updated as they are issued."
        />

        <div className="grid lg:grid-cols-2 gap-5 items-stretch">
          {/* Left: News & Updates */}
          <div className="flex flex-col bg-white dark:bg-white/[0.04] border border-black/[0.07] dark:border-white/10 rounded-2xl shadow-[0_10px_30px_-16px_rgba(0,0,0,0.3)] overflow-hidden">
            {/* Header */}
            <div className="flex items-center justify-between px-5 py-4 border-b border-black/[0.06] dark:border-white/10 bg-black/[0.015] dark:bg-white/[0.02]">
              <div className="flex items-center gap-2.5">
                <div className="w-8 h-8 rounded-lg bg-[var(--portal-accent-soft)] flex items-center justify-center">
                  <i className="ri-newspaper-line text-[color:var(--portal-accent)] text-base" />
                </div>
                <h3 className="text-[15px] font-semibold text-gray-800 dark:text-white">{t('announcements.news')}</h3>
              </div>
              <a href="#" className="text-xs font-medium text-[color:var(--portal-accent)] hover:text-[color:var(--portal-accent-dark)] transition-colors flex items-center gap-1">
                {t('announcements.viewAll')}
                <i className="ri-arrow-right-s-line text-sm" />
              </a>
            </div>

            {/* Tabs */}
            <div className="flex items-center gap-1 px-5 pt-3 pb-0 border-b border-gray-100 dark:border-white/[0.07]">
              {tabItems.map((tab) => (
                <button
                  key={tab.id}
                  onClick={() => setActiveTab(tab.id)}
                  className={`px-3 py-2 text-sm font-medium cursor-pointer transition-all duration-200 border-b-2 whitespace-nowrap ${
                    activeTab === tab.id
                      ? 'border-[color:var(--portal-accent)] text-[color:var(--portal-accent)]'
                      : 'border-transparent text-gray-400 dark:text-white/45 hover:text-gray-600 dark:hover:text-white/80'
                  }`}
                >
                  {t(tab.labelKey)}
                </button>
              ))}
            </div>

            {/* List */}
            <div className="flex-1 px-5 py-2">
              {getItems().map((item, i) => (
                <div
                  key={i}
                  className={`group flex items-center gap-3 py-2.5 cursor-pointer rounded-lg px-2 -mx-2 transition-all duration-200 hover:bg-[var(--portal-accent-soft)] dark:hover:bg-white/[0.06] ${
                    i < getItems().length - 1 ? 'border-b border-gray-100 dark:border-white/[0.07]' : ''
                  }`}
                >
                  <div className="w-1.5 h-1.5 bg-[var(--portal-accent)] rounded-full flex-shrink-0" />
                  {item.status && (
                    <span className={`text-[10px] px-2 py-0.5 rounded-full font-medium flex-shrink-0 ${tenderStatusColor[item.status]}`}>
                      {item.status}
                    </span>
                  )}
                  <span className="text-sm text-gray-700 dark:text-white/75 group-hover:text-[color:var(--portal-accent)] dark:group-hover:text-[color:var(--portal-accent-light)] transition-colors duration-200 truncate flex-1">
                    {item.title}
                  </span>
                  <span className="text-[11px] text-gray-400 flex-shrink-0">{formatDate(item.date)}</span>
                </div>
              ))}
            </div>
          </div>

          {/* Right: Important Notices */}
          <div className="flex flex-col bg-white dark:bg-white/[0.04] border border-black/[0.07] dark:border-white/10 rounded-2xl shadow-[0_10px_30px_-16px_rgba(0,0,0,0.3)] overflow-hidden">
            <div className="flex items-center justify-between px-5 py-4 border-b border-black/[0.06] dark:border-white/10 bg-black/[0.015] dark:bg-white/[0.02]">
              <div className="flex items-center gap-2.5">
                <div className="w-8 h-8 rounded-lg bg-red-50 flex items-center justify-center">
                  <i className="ri-alarm-warning-line text-red-500 text-base" />
                </div>
                <h3 className="text-[15px] font-semibold text-gray-800 dark:text-white">{t('announcements.notices')}</h3>
              </div>
              <a href="#" className="text-xs font-medium text-[color:var(--portal-accent)] hover:text-[color:var(--portal-accent-dark)] transition-colors flex items-center gap-1">
                {t('announcements.viewAll')}
                <i className="ri-arrow-right-s-line text-sm" />
              </a>
            </div>

            <div className="flex-1 px-5 py-2">
              {importantNotices.map((notice, i) => (
                <div
                  key={i}
                  className={`group flex items-center gap-3 py-2.5 cursor-pointer rounded-lg px-2 -mx-2 transition-all duration-200 hover:bg-[var(--portal-accent-soft)] dark:hover:bg-white/[0.06] ${
                    i < importantNotices.length - 1 ? 'border-b border-gray-100 dark:border-white/[0.07]' : ''
                  }`}
                >
                  <div className={`w-1.5 h-1.5 rounded-full flex-shrink-0 ${notice.urgent ? 'bg-red-500' : 'bg-[var(--portal-accent)]'}`} />
                  <span
                    className={`text-[10px] px-2 py-0.5 rounded-full font-medium flex-shrink-0 ${
                      notice.urgent ? 'bg-red-100 text-red-600' : 'bg-[var(--portal-accent-soft2)] text-[color:var(--portal-accent-dark)]'
                    }`}
                  >
                    {notice.urgent ? 'URGENT' : 'NOTICE'}
                  </span>
                  <span className="text-sm text-gray-700 dark:text-white/75 group-hover:text-[color:var(--portal-accent)] dark:group-hover:text-[color:var(--portal-accent-light)] transition-colors duration-200 truncate flex-1">
                    {notice.title}
                  </span>
                </div>
              ))}
            </div>
          </div>
        </div>
      </div>
    </section>
  );
};
